import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Card, Alert, Spinner } from "react-bootstrap";
import { useAuth } from "../../hooks/useAuth";
import { useQuery } from "react-query";
import { fetchActionsUtil } from "../../utils/helpers";
import { toast } from "react-toastify";

function ParticipantsSelectionModal({
  show,
  onHide,
  onSuccess,
  enrollmentId = null,
}) {
  const { backendUrl, token } = useAuth();
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [defaultSelectedIds, setDefaultSelectedIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch enrollment with participants
  const fetchEnrollment = async () => {
    try {
      if (!enrollmentId) return null;

      const data = await fetchActionsUtil(
        `${backendUrl}/api/partnership/course-enrollments/${enrollmentId}`,
        "GET",
        token,
      );

      return data.data;
    } catch (error) {
      console.error("Error fetching enrollment:", error);
      throw error;
    }
  };

  const { data: enrollmentData, isLoading } = useQuery(
    ["GET_ENROLLMENT_PARTICIPANTS", enrollmentId],
    () => fetchEnrollment(),
    {
      enabled: show && !!enrollmentId,
      keepPreviousData: true,
    },
  );

  // Auto-select participants with Completed or Certificate Issued status
  useEffect(() => {
    if (show && enrollmentData?.participants) {
      const autoSelectedIds = enrollmentData.participants
        .filter(
          (p) =>
            p.course_enrollment_status === "Completed" ||
            p.course_enrollment_status === "Certificate Issued",
        )
        .map((p) => p.id);
      setSelectedParticipants(autoSelectedIds);
      setDefaultSelectedIds(autoSelectedIds);
    }
  }, [show, enrollmentData]);

  const handleParticipantToggle = (participantId) => {
    setSelectedParticipants((prev) => {
      if (prev.includes(participantId)) {
        return prev.filter((id) => id !== participantId);
      } else {
        return [...prev, participantId];
      }
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allParticipantIds =
        enrollmentData?.participants?.map((p) => p.id) || [];
      setSelectedParticipants(allParticipantIds);
    } else {
      setSelectedParticipants([]);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Build array of objects with participantId and status
      // Match the same logic as badge display
      const participantsWithStatus = enrollmentData.participants.map(
        (participant) => {
          const isCurrentlySelected =
            selectedParticipants.includes(participant.id);
          const wasDefaultSelected = defaultSelectedIds.includes(participant.id);
          const isManuallChecked = isCurrentlySelected && !wasDefaultSelected;
          const isManuallyUnchecked =
            !isCurrentlySelected && wasDefaultSelected;

          let status;

          if (isManuallChecked) {
            status = "Completed";
          } else if (isManuallyUnchecked) {
            status = "Enrolled";
          } else {
            status = participant.course_enrollment_status || "Enrolled";
          }

          return {
            participantId: participant.id,
            status,
          };
        },
      );

      console.log("Participants with status:", participantsWithStatus);
      const completedCount = participantsWithStatus.filter(
        (p) => p.status === "Completed",
      ).length;
      toast.success(
        `${completedCount} participant(s) marked as Completed`,
      );
      onSuccess?.(participantsWithStatus);
      handleClose();
    } catch (error) {
      toast.error("Error processing participants");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedParticipants([]);
    onHide();
  };

  const allParticipantIds =
    enrollmentData?.participants?.map((p) => p.id) || [];
  const isAllSelected =
    allParticipantIds.length > 0 &&
    allParticipantIds.every((id) => selectedParticipants.includes(id));

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title as={"h6"}>Select Participants</Modal.Title>
      </Modal.Header>

      {isLoading ? (
        <Modal.Body className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading participants...</p>
        </Modal.Body>
      ) : (
        <>
          <Modal.Body>
            {!enrollmentData?.participants ||
            enrollmentData.participants.length === 0 ? (
              <Alert variant="info">
                No participants found for this enrollment
              </Alert>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="select-all"
                    label="Select All Participants"
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </Form.Group>

                <div className="participants-list">
                  {enrollmentData.participants.map((participant) => (
                    <Card key={participant.id} className="mb-2">
                      <Card.Body className="p-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <Form.Check
                            type="checkbox"
                            id={`participant-${participant.id}`}
                            label={`${participant.full_name} (${
                              participant.email
                            })`}
                            checked={selectedParticipants.includes(
                              participant.id,
                            )}
                            onChange={() =>
                              handleParticipantToggle(participant.id)
                            }
                          />
                          {(() => {
                            const isCurrentlySelected =
                              selectedParticipants.includes(participant.id);
                            const wasDefaultSelected =
                              defaultSelectedIds.includes(participant.id);
                            const isManuallChecked =
                              isCurrentlySelected && !wasDefaultSelected;
                            const isManuallyUnchecked =
                              !isCurrentlySelected && wasDefaultSelected;

                            let displayStatus, badgeColor;

                            if (isManuallChecked) {
                              displayStatus = "Completed";
                              badgeColor = "bg-success";
                            } else if (isManuallyUnchecked) {
                              displayStatus = "Enrolled";
                              badgeColor = "bg-info";
                            } else {
                              displayStatus =
                                participant.course_enrollment_status ||
                                "Enrolled";
                              badgeColor =
                                displayStatus === "Completed"
                                  ? "bg-success"
                                  : "bg-info";
                            }

                            return (
                              <span className={`badge ms-2 ${badgeColor}`}>
                                {displayStatus}
                              </span>
                            );
                          })()}
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>

                {/* <Alert variant="info" className="mt-3">
                  <strong>Selected:</strong> {selectedParticipants.length} of{" "}
                  {enrollmentData.participants.length} participant(s)
                </Alert> */}
              </>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Processing...
                </>
              ) : (
                `Confirm`
              )}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default ParticipantsSelectionModal;
